
import { Typography } from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as loadingKeys from '../../../../../actions/DashboardDomain/LoadingKeys';
import allActions from '../../../../../actions/index';
import { getConvertedPrice, getCurrency } from '../../../../../utils/DateAndNumberFunctions';
import { MODES } from '../../../../../utils/GlobalConstants';
import { formatLargeNumber } from '../../../../MaterialDetails/tabElements/TabComponents/tabGraph/TabGraphHelpers';
import { filterWidgetData, getOptInvTot } from '../utils/WidgetFunctions';
import { widgetTypeKeys } from '../utils/widgetTypes';

const TextWidgetContainer = ({ widgetID }) => {
  const dispatch = useDispatch();
  const settingsLoaded = useSelector((state) => state.commonState.loaded?.userSettings);
  const selectedPlants = useSelector((state) => state.commonState.selectedPlants);
  const selectedCCs = useSelector((state) => state.commonState.selectedCompanyCodes);
  const dashboardMode = useSelector((state) => state.commonState.dashboardMode);
  const inventoryValue = useSelector((state) => state.dashboardState[widgetID]);
  const loading = useSelector((state) => state.dashboardState.loading);
  const defaultCurrency = useSelector((state) => state.commonState?.userSettings?.DEFAULT_CURRENCY);
  const dashboardFilter = useSelector((state) => state.commonState.localDashboardFilters);
  const currencyList = useSelector((state) => state.commonState?.currencyList);
  const dataSrc = useSelector((state) => state.commonState?.dataSrc?.dashboard);
  const [inventoryStatus, setInventoryStatus] = useState({});
  const [widgetValue, setWidgetValue] = useState('');

  const { Text } = Typography;

  const selectedExists = () => {
    if (selectedCCs?.length === 0 && selectedPlants?.length === 0 && inventoryValue?.A) {
      return true;
    }
    if (dashboardMode === MODES.PLANT) {
      const key = `P${selectedPlants?.sort().join('')}`;
      return !!inventoryValue?.[key];
    }
    const key = `CC${selectedCCs?.sort().join('')}`;
    return !!inventoryValue?.[key];
  };

  const updateCache = () => {
    const tempInventory = JSON.parse(JSON.stringify(inventoryValue));
    if (selectedCCs?.length === 0 && selectedPlants?.length === 0) {
      setInventoryStatus(tempInventory.A);
    } else if (dashboardMode === MODES.PLANT) {
      setInventoryStatus(tempInventory[`P${selectedPlants?.sort().join('')}`]);
    } else {
      setInventoryStatus(tempInventory[`CC${selectedCCs?.sort().join('')}`]);
    }
  };

  useEffect(() => {
    if (selectedPlants && selectedCCs && settingsLoaded && dashboardMode
      && (!!dataSrc)) {
      if (selectedExists()) {
        updateCache();
      } else {
        if (loading?.[loadingKeys[widgetID]] === true) return;
        dispatch(allActions.DashboardActions.setLoading(loadingKeys[widgetID]));
        if (widgetID === widgetTypeKeys.inventoryValue) {
          dispatch(allActions.DashboardActions.inventoryValue(
            dashboardMode === MODES.PLANT ? selectedPlants : null,
            dashboardMode === MODES.CC ? selectedCCs : null,
            dashboardMode,
            null,
            dataSrc,
          ));
        }
        if (widgetID === widgetTypeKeys.optInvTot) {
          dispatch(allActions.DashboardActions.getOptInvTot(
            dashboardMode === MODES.PLANT ? selectedPlants : null,
            dashboardMode === MODES.CC ? selectedCCs : null,
            dashboardMode,
            dataSrc,
          ));
        }
        if (widgetID === widgetTypeKeys.totalExcessStock) {
          dispatch(allActions.DashboardActions.getTotalExcessStock(
            dashboardMode === MODES.PLANT ? selectedPlants : null,
            dashboardMode === MODES.CC ? selectedCCs : null,
            dashboardMode,
            dataSrc,
          ));
        }
      }
    }
  }, [selectedPlants, selectedCCs, settingsLoaded, dataSrc,
    dashboardMode, inventoryValue, dashboardFilter]);

  useEffect(() => {
    if (inventoryStatus?.data?.length > 0) {
      const currenctCurrency = defaultCurrency ?? 'NOK';
      const conversionData = currencyList?.find(
        (x) => currenctCurrency === x?.FROM_CURRENCY,
      );
      const conversionValues = conversionData
        ? [conversionData.FROM_RATIO, conversionData.TO_RATIO, conversionData.EXCHANGE_RATE]
        : [1, 1, 1];

      if (widgetID === widgetTypeKeys.optInvTot) {
        const optTot = getOptInvTot(inventoryStatus, dashboardFilter)
          ?.green?.toFixed(2);
        setWidgetValue(`${optTot} %`);
      } else {
        let val;
        if (widgetID === widgetTypeKeys.totalExcessStock
          || widgetID === widgetTypeKeys.inventoryValue) {
          const filteredData = filterWidgetData(
            inventoryStatus?.data, dashboardFilter,
          );
          val = filteredData?.reduce(((prev, curr) => prev + (curr?.INVENTORY_VALUE ?? 0)), 0);
        }
        val = getConvertedPrice(val, ...conversionValues);
        setWidgetValue(
          `${formatLargeNumber(val, true)} ${getCurrency(val, defaultCurrency)}`,
        );
      }
    }
  }, [inventoryStatus, dashboardFilter]);

  return (
    inventoryValue && !loading?.[loadingKeys?.optInvTot]
        && ((dashboardMode === MODES.PLANT && _.isEqual(inventoryStatus?.plants, selectedPlants))
          || (dashboardMode === MODES.CC && _.isEqual(inventoryStatus?.companyCodes, selectedCCs)))
      ? (
        <Text
          style={{
            fontWeight: 'bold',
            fontSize: 'max(0.78125vw, calc((0.7812 * 1.7778) * 1vh))',
            color: 'var(--neutral--100)'
          }}
        >
          Today:
          {' '}
          {widgetValue}
        </Text>
      )
      : <></>
  );
};

export default TextWidgetContainer;

TextWidgetContainer.propTypes = {
  widgetID: PropTypes.string.isRequired,
};
